import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import axios from '../../dataClient/dataClient'
import { ErrorMessage } from '../common/UserMessage'
import { FlexDialogContent, FlexEndContainer, StyledDialog } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle } from '../styles/Text'

const emptyPayload = {
  name: '',
  body: ''
}

const textOptions = {
  creating: {
    heading: 'Create New Communication Template',
    body: 'Creating this template will add it for all users.',
    error: 'There was an error creating the template. Please try again.',
    button: 'Create'
  },
  saving: {
    heading: 'Save Communication Template',
    body: 'Saving this template will change the template for all users.',
    error: 'There was an error saving the template. Please try again.',
    button: 'Save'
  }
}

function SaveCommunicationTemplateButton({
  payload = emptyPayload,
  templateId,
  open = false,
  disabled = false,
  onClose = () => {},
  onSuccess = () => {}
}) {
  const isSavingRef = useRef(false)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const persistedTemplateId = String(templateId ?? '').trim()
  const isCreatingTemplate = persistedTemplateId === ''
  const text = isCreatingTemplate ? textOptions.creating : textOptions.saving

  useEffect(() => {
    if (!open) return

    setError(false)
    setIsLoading(false)
  }, [open])

  function handleSave() {
    if (isSavingRef.current) return

    isSavingRef.current = true
    setError(false)
    setIsLoading(true)

    const request = isCreatingTemplate
      ? axios.post('/communication/communication-template', payload)
      : axios.patch(`/communication/communication-template/${persistedTemplateId}`, payload)

    request
      .then(({ data }) => {
        onSuccess(data.data)
        onClose()
      })
      .catch(e => {
        console.error('Failed to save the communication template', e)
        isSavingRef.current = false
        setError(true)
      })
      .finally(() => {
        isSavingRef.current = false
        setIsLoading(false)
      })
  }

  return (
    <>
      <Button type="submit" disabled={disabled}>
        Save
      </Button>
      <StyledDialog fullWidth open={open} onClose={onClose} keepMounted={false} aria-labelledby="save-template-title">
        <FlexDialogContent>
          <BoldFormTitle id="save-template-title">
            {text.heading}: {payload.name}
          </BoldFormTitle>
          <BodyText>{text.body}</BodyText>
          {error && <ErrorMessage>{text.error}</ErrorMessage>}
          <FlexEndContainer>
            <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </SecondaryButton>
            <Button type="button" onClick={handleSave} loading={isLoading}>
              {text.button}
            </Button>
          </FlexEndContainer>
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

SaveCommunicationTemplateButton.propTypes = {
  payload: PropTypes.shape({
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired
  }),
  templateId: PropTypes.string,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}

export default SaveCommunicationTemplateButton
